import { Bell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useOrderNotifications } from '@/hooks/useOrderNotifications';
import { Link } from 'react-router-dom';
import React from 'react';

export function OrderNotificationBadge() {
  const { newOrderCount, latestOrder, clearNotifications } = useOrderNotifications();
  const [open, setOpen] = React.useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {newOrderCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-destructive text-white animate-pulse"
            >
              {newOrderCount > 9 ? '9+' : newOrderCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">New Orders</h4>
            {newOrderCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearNotifications}
                className="h-auto p-1 text-xs"
              >
                Clear
              </Button>
            )}
          </div>

          {newOrderCount === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No new orders</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {latestOrder?.customer_name || 'New Customer'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {latestOrder?.created_at && formatDate(latestOrder.created_at)}
                    </p>
                  </div>
                  <Badge variant="secondary" className="ml-2">
                    {latestOrder?.total && formatCurrency(latestOrder.total)}
                  </Badge>
                </div>
              </div>

              {newOrderCount > 1 && (
                <p className="text-xs text-muted-foreground text-center">
                  + {newOrderCount - 1} more {newOrderCount - 1 === 1 ? 'order' : 'orders'}
                </p>
              )}

              <Link to="/admin/orders" onClick={() => setOpen(false)}>
                <Button className="w-full" size="sm">
                  View All Orders
                </Button>
              </Link>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default OrderNotificationBadge;
